import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import QRCodeDialog from "./QRCodeDialog";
import HitchhikerDialog from "./HitchhikerDialog";

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding: 0 1rem;
  gap: 1rem;

  @media (max-width: 480px) {
    max-width: 320px;
    gap: 0.75rem;
  }

  @media (max-width: 360px) {
    max-width: 280px;
    gap: 0.5rem;
  }
`;

const CircularButton = styled(motion.button)`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  font-size: 1.2rem;
  color: #0a0a1a;
  font-weight: bold;

  &:hover {
    background: #f0f0f0;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
  }

  @media (max-width: 360px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
`;

const DownloadButton = styled(motion.button)`
  flex: 1;
  max-width: 200px;
  height: 48px;
  border-radius: 12px;
  background: white;
  color: #0a0a1a;
  border: none;
  font-weight: 900;
  font-family: "Inter", sans-serif;
  font-style: italic;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.9rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover {
    background: #f0f0f0;
  }

  @media (max-width: 480px) {
    height: 44px;
    font-size: 0.8rem;
    letter-spacing: 0.5px;
  }

  @media (max-width: 360px) {
    height: 40px;
    font-size: 0.75rem;
    letter-spacing: 0.25px;
  }
`;

const ShuffleIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;

  @media (max-width: 480px) {
    width: 18px;
    height: 18px;
  }

  @media (max-width: 360px) {
    width: 16px;
    height: 16px;
  }
`;

const QuestionIcon = styled.div`
  font-family: "Inter", sans-serif;
  font-weight: bold;
`;

const MobileButtonRow = ({
  currentNumber,
  onReasonChange,
  onDialogStateChange,
}) => {
  const [isShuffling, setIsShuffling] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isHitchhikerDialogOpen, setIsHitchhikerDialogOpen] = useState(false);

  const handleShuffle = () => {
    if (isShuffling || currentNumber === 0) {
      return;
    }

    setIsShuffling(true);

    // Generate random target within next 10 numbers for better UX
    const randomOffset = Math.floor(Math.random() * 10) + 1; // 1-10 numbers ahead
    let targetNumber = currentNumber + randomOffset;

    // Handle wrap-around if we go beyond 42
    if (targetNumber > 42) {
      targetNumber = targetNumber - 42;
    }

    // Simulate fast scrolling that gradually slows down
    let speed = 50;
    let currentIndex = currentNumber;

    const shuffle = () => {
      // Always move forward (since target is always ahead)
      currentIndex++;

      // Handle wrap-around
      if (currentIndex > 42) {
        currentIndex = 1;
      }

      // Update the display
      onReasonChange(currentIndex, true); // true indicates it's during shuffling

      speed += 8; // Gradually slow down

      // Check if we should stop when we reach target
      const shouldStop = currentIndex === targetNumber;

      if (!shouldStop) {
        setTimeout(shuffle, speed);
      } else {
        // Final selection - make sure we end on target
        onReasonChange(targetNumber, false); // false means final selection
        setTimeout(() => {
          setIsShuffling(false);
        }, 300);
      }
    };

    shuffle();
  };

  const handleDownload = () => {
    // Placeholder for download functionality
    window.open("https://plumhq.app.link/?ref=42reasons", "_blank");
  };

  const handleHelp = () => {
    setIsHitchhikerDialogOpen(true);
    if (onDialogStateChange) onDialogStateChange(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    if (onDialogStateChange) onDialogStateChange(false);
  };

  const handleHitchhikerDialogClose = () => {
    setIsHitchhikerDialogOpen(false);
    if (onDialogStateChange) onDialogStateChange(false);
  };

  return (
    <>
      <ButtonContainer>
        <CircularButton
          onClick={e => {
            handleShuffle();
          }}
          disabled={isShuffling || currentNumber === 0}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <ShuffleIcon src="/Shuffle.svg" alt="Shuffle" />
        </CircularButton>

        <DownloadButton
          onClick={handleDownload}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Download App
        </DownloadButton>

        <CircularButton
          onClick={handleHelp}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <QuestionIcon>?</QuestionIcon>
        </CircularButton>
      </ButtonContainer>

      <QRCodeDialog isOpen={isDialogOpen} onClose={handleDialogClose} />
      <HitchhikerDialog
        isOpen={isHitchhikerDialogOpen}
        onClose={handleHitchhikerDialogClose}
      />
    </>
  );
};

export default MobileButtonRow;
